import {
  ReactNode,
  Ref,
  useState,
  useImperativeHandle,
  forwardRef,
  ReactElement,
  useRef,
} from "react";
import { OverlayPanel } from "primereact/overlaypanel";
import classNames from "classnames";
import useTranslations from "../../../core/i18n/useTranslations";

import styles from "./TableRowSelectionPopup.module.css";

/** Instance value that is exposed to parent components when using ref */
export declare type TableRowSelectionPopupActions<TRowData> = {
  open: (selectedItems: TRowData[]) => void;
  close: (clear?: boolean) => void;
  toggle: () => void;
};

export interface TableRowSelectionPopupProps<TRowData> {
  /* Optional. Render custom header. */
  renderHead?: (selectedItems: TRowData[]) => ReactNode;
  /* Required. Render custom body. */
  renderBody: (selectedItems: TRowData[]) => ReactNode;
}

/** Popup that should appear when table rows seleted */
const TableRowSelectionPopupInternal = <TRowData,>(
  { renderHead = undefined, renderBody }: TableRowSelectionPopupProps<TRowData>,
  ref: Ref<TableRowSelectionPopupActions<TRowData>>
) => {
  const t = useTranslations();
  const [selectedItems, setSelectedItems] = useState<TRowData[]>([]);
  const overlayRef = useRef<OverlayPanel>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(false);

  // declare limited interface exposed to parent components when using ref
  useImperativeHandle<
    TableRowSelectionPopupActions<TRowData>,
    TableRowSelectionPopupActions<TRowData>
  >(ref, () => {
    const actions: TableRowSelectionPopupActions<TRowData> = {
      open: function open(selectedItemsT) {
        if (selectedItemsT.length === 0) {
          overlayRef.current?.hide();
          setIsOpen(false);
          isOpenRef.current = false;
          setSelectedItems([]);
        } else {
          setSelectedItems(selectedItemsT);
          if (!isOpenRef.current && triggerRef.current) {
            triggerRef.current.style.position = "fixed";
            triggerRef.current.style.left = "50%";
            triggerRef.current.style.bottom = "8px";
            triggerRef.current.style.top = "auto";
            triggerRef.current.click();
            setIsOpen(true);
            isOpenRef.current = true;
          }
        }
      },
      close: function close(clear?: boolean) {
        if (clear) {
          setSelectedItems([]);
        }
        overlayRef.current?.hide();
        setIsOpen(false);
        isOpenRef.current = false;
      },
      toggle: function toggle() {
        if (triggerRef.current) {
          triggerRef.current.click();
        }
        setIsOpen(!isOpen);
        isOpenRef.current = !isOpenRef.current;
      },
    };
    return actions;
  });

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        style={{ display: "none" }}
        aria-hidden="true"
        aria-label={t("Common:batch_trigger")}
        onClick={(e) => {
          if (!isOpenRef.current) {
            overlayRef.current?.show(e, triggerRef.current || undefined);
          }
        }}
      >
        {t("Common:batch_trigger")}
      </button>
      <OverlayPanel
        dismissable={false}
        closeOnEscape={false}
        ref={overlayRef}
        data-cy="table_row_selection_popup"
        className={classNames(styles.overlayPanel, { [styles.isOpen]: isOpen })}
        onShow={() => {
          setIsOpen(true);
          isOpenRef.current = true;
        }}
        onHide={() => {
          setIsOpen(false);
          isOpenRef.current = false;
        }}
        pt={{
          content: {
            className: styles.popupContent,
          },
        }}
      >
        <section
          aria-label={`Table row selection popup: ${selectedItems.length} ${
            selectedItems.length > 1
              ? t("Common:items_selected")
              : t("Common:item_selected")
          }`}
        >
          <div
            className={styles.popupHeader}
            data-cy="row_selection_popup_header"
          >
            {renderHead ? (
              renderHead(selectedItems)
            ) : (
              <div>
                {selectedItems.length}{" "}
                {selectedItems.length > 1
                  ? t("Common:items_selected")
                  : t("Common:item_selected")}
              </div>
            )}
          </div>
          <div className={styles.popupBody}>{renderBody(selectedItems)}</div>
        </section>
      </OverlayPanel>
    </>
  );
};

// use forwardRef and cast the exported component to valid type
export const TableRowSelectionPopup = forwardRef(
  TableRowSelectionPopupInternal
) as <TRowData>(
  p: TableRowSelectionPopupProps<TRowData> & {
    ref?: Ref<TableRowSelectionPopupActions<TRowData>>;
  }
) => ReactElement;

export default TableRowSelectionPopup;
